import { useEffect, useState, useMemo, useCallback } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { HOSE as DEFAULT_HOSE, APPLIANCES as DEFAULT_APPLIANCES, SB_TIPS as DEFAULT_TIPS } from '../constants';
import { usePersist } from './usePersist';

const DEFAULT_OPS = {
  elevFactor: 0.5,
  minInlet: 20,
  standpipeFloor: 5,
  standpipeAppliance: 25,
  defaultPumpPSI: 150,
  defaultPumpGPM: 1500,
};

const EMPTY_OVERRIDES = { hose: {}, appliances: {}, ops: {} };

// Merge a single section's overrides on top of a base, applying value sanitization.
function mergeSection(base, overrides, kind) {
  if (!overrides) return base;
  const out = { ...base };
  for (const [key, val] of Object.entries(overrides)) {
    if (typeof val !== 'number' || !isFinite(val)) continue;
    if (kind === 'hose') {
      if (out[key] && val > 0) out[key] = { ...out[key], c: val };
    } else if (kind === 'appliances') {
      if (out[key]) out[key] = { ...out[key], loss: Math.max(0, val) };
    } else if (kind === 'ops') {
      if (key in out) out[key] = Math.max(0, val);
    }
  }
  return out;
}

export function useCalcDefaults() {
  const [adminOverrides, setAdminOverrides] = useState({});
  const [sessionOverrides, setSessionOverrides] = usePersist('hydro_calc_session_overrides', EMPTY_OVERRIDES);

  useEffect(() => {
    return onSnapshot(doc(db, 'config', 'calcDefaults'),
      (snap) => {
        setAdminOverrides(snap.exists() ? snap.data() : {});
      },
      (err) => {
        // Fall back to built-in defaults on a read error.
        console.error('[useCalcDefaults] snapshot error:', err);
        setAdminOverrides({});
      },
    );
  }, []);

  // Admin-only merged values — what the admin panel set, before any local overrides.
  const adminDefaults = useMemo(() => {
    return {
      hose: mergeSection(DEFAULT_HOSE, adminOverrides.hose, 'hose'),
      appliances: mergeSection(DEFAULT_APPLIANCES, adminOverrides.appliances, 'appliances'),
      tips: { ...DEFAULT_TIPS },
      ops: mergeSection(DEFAULT_OPS, adminOverrides.ops, 'ops'),
    };
  }, [adminOverrides]);

  // Effective values — admin defaults with session overrides layered on top.
  const merged = useMemo(() => {
    return {
      hose: mergeSection(adminDefaults.hose, sessionOverrides.hose, 'hose'),
      appliances: mergeSection(adminDefaults.appliances, sessionOverrides.appliances, 'appliances'),
      tips: adminDefaults.tips,
      ops: mergeSection(adminDefaults.ops, sessionOverrides.ops, 'ops'),
    };
  }, [adminDefaults, sessionOverrides]);

  const setSessionOverride = useCallback((section, key, val) => {
    setSessionOverrides((prev) => {
      const next = { ...prev, [section]: { ...(prev[section] || {}), [key]: val } };
      return next;
    });
  }, [setSessionOverrides]);

  const resetSessionField = useCallback((section, key) => {
    setSessionOverrides((prev) => {
      const sec = { ...(prev[section] || {}) };
      delete sec[key];
      return { ...prev, [section]: sec };
    });
  }, [setSessionOverrides]);

  const resetAllSession = useCallback(() => {
    setSessionOverrides(EMPTY_OVERRIDES);
  }, [setSessionOverrides]);

  const hasSessionOverrides = useMemo(() => {
    const s = sessionOverrides || {};
    return Object.keys(s.hose || {}).length > 0
      || Object.keys(s.appliances || {}).length > 0
      || Object.keys(s.ops || {}).length > 0;
  }, [sessionOverrides]);

  return {
    defaults: merged,
    adminDefaults,
    sessionOverrides,
    setSessionOverride,
    resetSessionField,
    resetAllSession,
    hasSessionOverrides,
  };
}
